<template>
  <div class="space-y-6">
    <h2 class="text-2xl font-bold text-gray-900 flex items-center gap-2">
      <span>🎯</span>
      KPIs Principales
    </h2>

    <!-- Resumen General -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <KpiCard
        title="Total Ventas"
        :value="kpis.resumen.total_ventas"
        icon="🛍️"
        color="blue"
      />
      <KpiCard
        title="Total Productos"
        :value="kpis.resumen.total_productos"
        icon="📦"
        color="purple"
      />
      <KpiCard
        title="Unidades Totales"
        :value="kpis.resumen.unidades_totales.toFixed(1)"
        icon="📊"
        color="indigo"
      />
      <KpiCard
        title="Valor Total"
        :value="formatearMoneda(kpis.resumen.valor_total)"
        icon="💰"
        color="green"
      />
      <KpiCard
        title="Ticket Promedio"
        :value="formatearMoneda(kpis.resumen.ticket_promedio)"
        icon="🎫"
        color="yellow"
      />
    </div>

    <!-- Métricas de Producción, Instalación y Completado -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Producción -->
      <div :class="getSemaforoClass(kpis.produccion.estado)">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span>⚙️</span>
            Producción
          </h3>
          <SemaforoBadge :estado="kpis.produccion.estado" />
        </div>
        
        <div class="space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Unidades Producidas</span>
            <span class="text-xl font-bold text-gray-900">{{ kpis.produccion.unidades_producidas.toFixed(1) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Unidades Pendientes</span>
            <span class="text-xl font-bold text-orange-600">{{ kpis.produccion.unidades_pendientes.toFixed(1) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Promedio Días</span>
            <span class="text-lg font-semibold text-gray-700">{{ kpis.produccion.promedio_dias_produccion }} días</span>
          </div>
          <div class="mt-4 pt-3 border-t">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-700">Tasa de Completado</span>
              <span class="text-2xl font-bold text-gray-900">{{ kpis.produccion.tasa_completado_produccion.toFixed(1) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div 
                :class="getSemaforoBarClass(kpis.produccion.estado)"
                class="h-3 rounded-full transition-all duration-500"
                :style="`width: ${kpis.produccion.tasa_completado_produccion}%`"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Instalación -->
      <div :class="getSemaforoClass(kpis.instalacion.estado)">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span>🔧</span>
            Instalación
          </h3>
          <SemaforoBadge :estado="kpis.instalacion.estado" />
        </div>
        
        <div class="space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Unidades Instaladas</span>
            <span class="text-xl font-bold text-gray-900">{{ kpis.instalacion.unidades_instaladas.toFixed(1) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Unidades Pendientes</span>
            <span class="text-xl font-bold text-orange-600">{{ kpis.instalacion.unidades_pendientes.toFixed(1) }}</span>
          </div>
          <div class="mt-4 pt-3 border-t">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-700">Tasa de Completado</span>
              <span class="text-2xl font-bold text-gray-900">{{ kpis.instalacion.tasa_completado_instalacion.toFixed(1) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div 
                :class="getSemaforoBarClass(kpis.instalacion.estado)"
                class="h-3 rounded-full transition-all duration-500"
                :style="`width: ${kpis.instalacion.tasa_completado_instalacion}%`"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Completado General -->
      <div :class="getSemaforoClass(kpis.completado_general.estado)">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span>✅</span>
            Completado General
          </h3>
          <SemaforoBadge :estado="kpis.completado_general.estado" />
        </div>
        
        <div class="space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Ventas Completadas</span>
            <span class="text-xl font-bold text-green-600">{{ kpis.completado_general.ventas_completadas }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Ventas en Proceso</span>
            <span class="text-xl font-bold text-blue-600">{{ kpis.completado_general.ventas_en_proceso }}</span>
          </div>
          <div class="mt-4 pt-3 border-t">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-700">Tasa de Completado</span>
              <span class="text-2xl font-bold text-gray-900">{{ kpis.completado_general.tasa_completado_total.toFixed(1) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div 
                :class="getSemaforoBarClass(kpis.completado_general.estado)"
                class="h-3 rounded-full transition-all duration-500"
                :style="`width: ${kpis.completado_general.tasa_completado_total}%`"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KpiCard from './KpiCard.vue';
import SemaforoBadge from './SemaforoBadge.vue';

export default {
  name: 'KPIsSection',
  components: {
    KpiCard,
    SemaforoBadge
  },
  props: {
    kpis: {
      type: Object,
      required: true
    }
  },
  methods: {
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(valor);
    },
    getSemaforoClass(estado) {
      const classes = 'bg-white rounded-xl shadow-lg p-6 border-l-4 transition-all hover:shadow-xl';
      switch (estado) {
        case 'VERDE':
          return `${classes} border-green-500`;
        case 'AMARILLO':
          return `${classes} border-yellow-500`;
        case 'NARANJA':
          return `${classes} border-orange-500`;
        case 'ROJO':
          return `${classes} border-red-500`;
        default:
          return `${classes} border-gray-300`;
      }
    },
    getSemaforoBarClass(estado) {
      switch (estado) {
        case 'VERDE':
          return 'bg-gradient-to-r from-green-400 to-green-600';
        case 'AMARILLO':
          return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
        case 'NARANJA':
          return 'bg-gradient-to-r from-orange-400 to-orange-600';
        case 'ROJO':
          return 'bg-gradient-to-r from-red-400 to-red-600';
        default:
          return 'bg-gray-400';
      }
    }
  }
};
</script>
